import {
  AttendanceCode,
  AttendanceSessionEnum,
  JustificationBase,
  JustificationSave,
  SHORT_FORMATTED_DATE_FORMAT_FNS,
  WithAvatar,
  WithName,
} from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useJustificationChangeModal } from '@schooly/components/justifications';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { AbsenceNoticeConfirmContent } from '../../components/common/AbsenceNoticeInfo/AbsenceNoticeConfirmContent';
import {
  getJustificationChangeType,
  getResultingJustification,
  hasJustificationNote,
  shouldConfirmJustification,
} from './attendance-utils';

export type CodeChangeEntry = {
  session: AttendanceSessionEnum;
  attendance_code_id: string | null;
};

export type ConfirmCodeChangeParams = {
  newCode: AttendanceCode;
  codes: AttendanceCode[];
  entries: CodeChangeEntry[];
  sessions: AttendanceSessionEnum[];
  justification: JustificationBase | null;
  pendingNote: JustificationSave | null;
  absenceNotice: string | null;
  relation: WithName & WithAvatar;
  date: string;
};

export type ConfirmCodeChangeResult = {
  note: JustificationSave | null;
};

export const useJustificationCodeChange = () => {
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();
  const { confirmJustificationChange } = useJustificationChangeModal();

  const confirmCodeChange = useCallback(
    async ({
      newCode,
      codes,
      entries,
      sessions,
      justification,
      pendingNote,
      absenceNotice,
      relation,
      date,
    }: ConfirmCodeChangeParams): Promise<ConfirmCodeChangeResult | undefined> => {
      const resultingJustification = getResultingJustification({
        justification,
        note: pendingNote,
        codes,
        entries,
      });

      if (
        shouldConfirmJustification({
          newCode,
          hasJustification: !!resultingJustification,
          hasAbsenceNotice: !!absenceNotice,
          sessions,
        })
      ) {
        const confirmed = await getConfirmation({
          textId: 'attendance-justification-OpenWithAbsenceNotice-Title',
          textValues: {
            name:
              getUserFullName(relation) ||
              formatMessage({ id: 'attendance-justification-Student' }).toLowerCase(),
            date: format(newDateTimezoneOffset(date), SHORT_FORMATTED_DATE_FORMAT_FNS),
            session: sessions
              .map((session) => formatMessage({ id: `attendance-${session.toUpperCase()}` }))
              .join('&'),
            codeName: newCode.name,
          },
          content: <AbsenceNoticeConfirmContent comment={absenceNotice ?? ''} />,
        });
        if (!confirmed) return;
      }

      if (!justification) return { note: null };

      const changeType = getJustificationChangeType({
        status: justification.status,
        newCode,
        codes,
        entries,
        sessions,
      });

      if (!changeType) return { note: null };

      if (pendingNote && hasJustificationNote(pendingNote, changeType)) {
        return { note: pendingNote };
      }

      const reason = await confirmJustificationChange({
        changeType,
        codeId: newCode.id,
        relation,
        date,
        sessions,
        justification,
        entries,
      });
      if (!reason) return;

      return {
        note: changeType === 'close' ? { staff_reason: reason } : { staff_request: reason },
      };
    },
    [confirmJustificationChange, formatMessage, getConfirmation],
  );

  return { confirmCodeChange };
};
